import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'src/lib/routing';
import invariant from 'tiny-invariant';

import { LoadingOverlay } from 'components/common';
import { useIsKinde } from 'containers/Auth/AuthWrapper';
import { globalVariables } from 'lib/globalVariables';
import { getImpersonationToken } from 'utils/impersonation';

type Props = {
  query?: string;
  isKinde: boolean;
  authToken?: string;
  sessionsURL: string;
  params: Readonly<
    Partial<{
      '*': string;
    }>
  >;
};

export function constructSessionsPath({ params, sessionsURL, isKinde, authToken, query = '' }: Props): URL {
  invariant(params['*'], 'no redirect path ');

  if (isKinde) {
    return new URL(
      `${sessionsURL}/api/auth/login?post_login_redirect_url=${sessionsURL}/${params['*']}${query ? `?${query}` : ''}`,
    );
  }

  invariant(authToken, 'Auth token is required when not using legacy authentication');

  return new URL(`${sessionsURL}/${params['*']}?authToken=${authToken}${query ? `&${query}` : ''}`);
}

export function SessionsRedirect() {
  const params = useParams<{ '*': string }>();
  const isKinde = useIsKinde();
  const [query] = useSearchParams();
  const { getOrganization } = useKindeAuth();
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      // A tab opened from the impersonation flow carries the token in the URL fragment
      // (sessionStorage is tab-scoped and `noopener` severs the opener). Read it, strip
      // it immediately so it never lands in history, then fall back to sessionStorage.
      const hash = window.location.hash;
      let hashToken: string | null = null;
      if (hash.startsWith('#access_token=')) {
        hashToken = hash.slice('#access_token='.length);
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      const token = hashToken ?? getImpersonationToken();

      // A token means we're impersonating — forward it as authToken instead of going through Kinde SSO.
      const effectiveIsKinde = token ? false : isKinde;

      if (effectiveIsKinde) {
        const org = await getOrganization();
        query.append('org_code', org ?? '');
      }

      try {
        const redirectURL = constructSessionsPath({
          params,
          isKinde: effectiveIsKinde,
          sessionsURL: globalVariables.getEnvironmentVariables().sessionsAppURI,
          authToken: token ?? '',
          query: query.toString(),
        });

        window.location.replace(redirectURL.href);
      } catch (e) {
        console.error(e);
        setErr(e instanceof Error ? e.message : 'An error occurred while constructing the redirect URL');
      }
    })();
  }, [params, isKinde, query, getOrganization]);

  if (err) {
    return (
      <div className="my-10 flex h-full w-full justify-center">
        <h1 className="text-2xl font-bold">{err}</h1>
      </div>
    );
  }

  return <LoadingOverlay />;
}
